import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    DateField,
    FunctionField,
    EditButton,
    Filter,
    TextInput,
    SelectInput,
    TopToolbar,
    CreateButton,
    ExportButton,
    RefreshButton,
    useTranslate,
    useListContext,
    RecordContextProvider,
} from 'react-admin';
import { Chip, Box, Tooltip, Fab, Typography, useMediaQuery, useTheme } from '@mui/material';
import {
    CheckCircle,
    Cancel,
    Build,
    Error as ErrorIcon,
    CloudOff,
    Schedule,
    VpnKey,
    Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const OcppServersFilter = (props: any) => {
    const translate = useTranslate();

    return (
        <Filter {...props}>
            <TextInput
                label={translate('resources.ocpp-servers.list.filters.search')}
                source="search"
                alwaysOn
            />
            <SelectInput
                source="status"
                label={translate('resources.ocpp-servers.fields.status')}
                choices={[
                    { id: 'pending', name: 'Pending' },
                    { id: 'active', name: 'Active' },
                    { id: 'inactive', name: 'Inactive' },
                    { id: 'maintenance', name: 'Maintenance' },
                    { id: 'retired', name: 'Retired' },
                ]}
            />
        </Filter>
    );
};

const DesktopListActions = () => (
    <TopToolbar>
        <CreateButton />
        <ExportButton />
        <RefreshButton />
    </TopToolbar>
);

const MobileListActions = () => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0 }}>
        <ExportButton />
    </Box>
);

const getStatusConfig = (status: string) => {
    switch (status?.toLowerCase()) {
        case 'active':
            return {
                color: '#009A75',
                backgroundColor: '#E8F5E8',
                icon: CheckCircle,
            };
        case 'pending':
            return {
                color: '#FF9800',
                backgroundColor: '#FFF3E0',
                icon: Schedule,
            };
        case 'inactive':
            return {
                color: '#9E9E9E',
                backgroundColor: '#F5F5F5',
                icon: Cancel,
            };
        case 'maintenance':
            return {
                color: '#FF9800',
                backgroundColor: '#FFF3E0',
                icon: Build,
            };
        case 'retired':
            return {
                color: '#757575',
                backgroundColor: '#EEEEEE',
                icon: CloudOff,
            };
        default:
            return {
                color: '#9E9E9E',
                backgroundColor: '#F5F5F5',
                icon: ErrorIcon,
            };
    }
};

const StatusField = ({ record }: { record: any }) => {
    const translate = useTranslate();
    const config = getStatusConfig(record?.status);
    const StatusIcon = config.icon;

    return (
        <Chip
            icon={<StatusIcon sx={{ fontSize: 16 }} />}
            label={translate(`resources.ocpp-servers.statuses.${record?.status?.toLowerCase()}`)}
            size="small"
            sx={{
                backgroundColor: config.backgroundColor,
                color: config.color,
                fontWeight: 500,
                '& .MuiChip-icon': {
                    color: config.color,
                },
            }}
        />
    );
};

const LoadGroupCountField = ({ record }: { record: any }) => {
    const count = record?.loadGroupCount || 0;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <span style={{ fontWeight: count > 0 ? 600 : 400 }}>
                {count} {count === 1 ? 'group' : 'groups'}
            </span>
        </Box>
    );
};

const EndpointUrlField = ({ record }: { record: any }) => {
    const url = record?.endpointUrl || '';

    try {
        const urlObj = new URL(url);
        return (
            <Box sx={{ fontSize: '0.875rem', fontFamily: 'monospace' }}>
                {urlObj.host}
            </Box>
        );
    } catch {
        return (
            <Box sx={{ fontSize: '0.875rem', fontFamily: 'monospace', color: '#666' }}>
                {url}
            </Box>
        );
    }
};

const ApiKeyField = ({ record }: { record: any }) => {
    const translate = useTranslate();
    const apiKeyDisplay = record?.api_key_display || record?.apiKeyDisplay;

    if (!apiKeyDisplay) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#999' }}>
                <VpnKey sx={{ fontSize: 14 }} />
                <span style={{ fontSize: '0.75rem' }}>Not configured</span>
            </Box>
        );
    }

    return (
        <Tooltip title={translate('resources.ocpp-servers.fields.apiKey')}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <VpnKey sx={{ fontSize: 14, color: '#00C853' }} />
                <span style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                    {apiKeyDisplay}
                </span>
            </Box>
        </Tooltip>
    );
};

const ElkKeyField = ({ record }: { record: any }) => {
    const translate = useTranslate();
    const elkKeyDisplay = record?.elk_key_display;

    if (!elkKeyDisplay) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#999' }}>
                <VpnKey sx={{ fontSize: 14 }} />
                <span style={{ fontSize: '0.75rem' }}>Not configured</span>
            </Box>
        );
    }

    return (
        <Tooltip title={translate('resources.ocpp-servers.fields.elkKey', 'ELK API Key')}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <VpnKey sx={{ fontSize: 14, color: '#FF9800' }} />
                <span style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                    {elkKeyDisplay}
                </span>
            </Box>
        </Tooltip>
    );
};

const MobileList = () => {
    const { data, isLoading } = useListContext();
    const navigate = useNavigate();
    const translate = useTranslate();

    if (isLoading || !data) return null;

    return (
        <Box>
            {data.map((record: any) => (
                <RecordContextProvider key={record.id} value={record}>
                    <Box
                        onClick={() => navigate(`/ocpp-servers/${record.id}`)}
                        sx={{
                            py: 1.5,
                            px: 2,
                            borderBottom: '1px solid #F0F0F0',
                            cursor: 'pointer',
                            '&:active': { backgroundColor: '#F5F5F5' },
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                    fontSize: { xs: '0.9rem', sm: '0.95rem' },
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '60%',
                                }}
                            >
                                {record.name}
                            </Typography>
                            <StatusField record={record} />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography
                                sx={{
                                    fontSize: { xs: '0.75rem', sm: '0.8rem' },
                                    color: '#666',
                                    fontFamily: 'monospace',
                                }}
                            >
                                {record.version} &middot; {record.loadGroupCount || 0} {(record.loadGroupCount || 0) === 1 ? 'group' : 'groups'}
                            </Typography>
                            <Typography sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, color: '#999' }}>
                                {record.createdAt ? new Date(record.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                            </Typography>
                        </Box>
                    </Box>
                </RecordContextProvider>
            ))}
        </Box>
    );
};

export const OcppServersList = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();

    return (
        <>
            <List
                filters={<OcppServersFilter />}
                actions={isMobile ? <MobileListActions /> : <DesktopListActions />}
                empty={isMobile ? false : undefined}
                perPage={25}
                sort={{ field: 'createdAt', order: 'DESC' }}
                sx={{
                    '& .RaList-content': {
                        backgroundColor: isMobile ? 'transparent !important' : '#FAFAFA',
                        ...(isMobile && { boxShadow: 'none !important', mt: 2 }),
                    },
                    ...(isMobile && {
                        '& .RaList-actions': {
                            backgroundColor: 'transparent !important',
                            p: '0 !important',
                            minHeight: 'auto !important',
                        },
                        '& .RaListToolbar-root': {
                            backgroundColor: 'transparent !important',
                            flexDirection: 'row !important',
                            flexWrap: 'wrap !important',
                            alignItems: 'center !important',
                            gap: '8px !important',
                        },
                        '& .RaListToolbar-root > span': { display: 'none' },
                        '& .RaListToolbar-root > form, & .RaFilterForm-root': {
                            flex: '1 1 100% !important',
                            width: '100% !important',
                        },
                        '& .filter-field, & .RaFilterForm-filterFormInput': {
                            width: '100% !important',
                        },
                        '& .filter-field .MuiFormControl-root': { width: '100% !important' },
                        '& .filter-field .MuiInputBase-root': { width: '100% !important' },
                        '& .MuiToolbar-root': {
                            backgroundColor: 'transparent !important',
                            minHeight: 'auto !important',
                        },
                    }),
                }}
            >
                {isMobile ? (
                    <MobileList />
                ) : (
                    <Datagrid
                        bulkActionButtons={false}
                        sx={{
                            '& .RaDatagrid-headerCell': {
                                backgroundColor: '#F5F5F5',
                                fontWeight: 600,
                            },
                            '& .RaDatagrid-row': {
                                '&:hover': {
                                    backgroundColor: '#F0F7FF',
                                },
                            },
                        }}
                    >
                        <TextField
                            source="id"
                            label={translate('resources.ocpp-servers.fields.id', 'Server ID')}
                            sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.875rem',
                            }}
                        />

                        <TextField source="name" />

                        <FunctionField
                            source="status"
                            label={translate('resources.ocpp-servers.fields.status')}
                            render={(record: any) => <StatusField record={record} />}
                        />

                        <TextField
                            source="version"
                            label={translate('resources.ocpp-servers.fields.version')}
                        />

                        <FunctionField
                            source="loadGroupCount"
                            label={translate('resources.ocpp-servers.fields.loadGroups')}
                            render={(record: any) => <LoadGroupCountField record={record} />}
                        />

                        <DateField
                            source="lastHeartbeat"
                            label={translate('resources.ocpp-servers.fields.lastHeartbeat')}
                            options={{
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                            }}
                            emptyText="Never"
                        />

                        <DateField
                            source="createdAt"
                            options={{
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                            }}
                        />

                        <EditButton />
                    </Datagrid>
                )}
            </List>
            {isMobile && (
                <Fab
                    color="primary"
                    onClick={() => navigate('/ocpp-servers/create')}
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        backgroundColor: '#009A75',
                        '&:hover': { backgroundColor: '#007A5E' },
                        zIndex: 1100,
                    }}
                >
                    <AddIcon />
                </Fab>
            )}
        </>
    );
};
